/**
 * MCAP Logs API Service
 * Centralized API calls for MCAP log file management
 */

// const RAAS_DATA_SERVICE_URL = "http://localhost:8000/api/v1";

import { servicesUrlConfig } from "shared/config/setting";

const RAAS_DATA_SERVICE_URL = servicesUrlConfig.RAASDATA_API.base_url;

/**
 * Append the optional inclusive time window the stats routes share:
 * `uploaded_*` bound the upload time, `recorded_*` the recording start, as
 * ISO-8601 UTC instants.
 */
const appendTimeWindow = (
  queryParams,
  { uploaded_from, uploaded_to, recorded_from, recorded_to }
) => {
  if (uploaded_from) queryParams.append("uploaded_from", uploaded_from);
  if (uploaded_to) queryParams.append("uploaded_to", uploaded_to);
  if (recorded_from) queryParams.append("recorded_from", recorded_from);
  if (recorded_to) queryParams.append("recorded_to", recorded_to);
};

export const mcapLogsService = {
  /**
   * Get all MCAP log files with pagination and filters
   * @param {function} authFetch - Authenticated fetch function
   * @param {Object} params - Query parameters. `sort_by` must be one of the
   *   backend's MCAPLogSortField values and `sort_order` is "asc" | "desc";
   *   both are omitted when unset so the API applies its own defaults.
 *   `upload_state`, `processing_state` and `recording_state` are lifecycle
 *   state values, `tasks` and `scene_types` match ANY of the given names, and
 *   the `uploaded_from/to` + `recorded_from/to` bounds are inclusive ISO-8601
 *   UTC instants.
   * @returns {Promise} API response with MCAP logs data
   */
  async getAll(authFetch, params = {}) {
    const {
      skip = 0,
      limit = 100,
      robot_id,
      org_id,
      tasks,
      objects,
      scene_types,
      location,
      tags,
      uploaded_by,
      processed,
      upload_state,
      processing_state,
      recording_state,
      uploaded_from,
      uploaded_to,
      recorded_from,
      recorded_to,
      topic,
      storage_id,
      replica_status,
      ingested,
      sort_by,
      sort_order,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append("skip", skip);
    queryParams.append("limit", limit);

    if (robot_id) queryParams.append("robot_id", robot_id);
    if (org_id) queryParams.append("org_id", org_id);
    if (tasks && tasks.length > 0) {
      tasks.forEach((task) => queryParams.append("tasks", task));
    }
    if (objects && objects.length > 0) {
      objects.forEach((obj) => queryParams.append("objects", obj));
    }
    if (scene_types && scene_types.length > 0) {
      scene_types.forEach((scene) => queryParams.append("scene_types", scene));
    }
    if (location) queryParams.append("location", location);
    if (tags && tags.length > 0) {
      tags.forEach((tag) => queryParams.append("tags", tag));
    }
    if (uploaded_by) queryParams.append("uploaded_by", uploaded_by);
    if (processed !== undefined) queryParams.append("processed", processed);
    if (upload_state) queryParams.append("upload_state", upload_state);
    if (processing_state) queryParams.append("processing_state", processing_state);
    if (recording_state) queryParams.append("recording_state", recording_state);
    // Inclusive time windows, ISO-8601 UTC instants. uploaded_* bound the
    // upload time, recorded_* the recording START time.
    if (uploaded_from) queryParams.append("uploaded_from", uploaded_from);
    if (uploaded_to) queryParams.append("uploaded_to", uploaded_to);
    if (recorded_from) queryParams.append("recorded_from", recorded_from);
    if (recorded_to) queryParams.append("recorded_to", recorded_to);
    if (topic) queryParams.append("topic", topic);
    if (storage_id) queryParams.append("storage_id", storage_id);
    if (replica_status) queryParams.append("replica_status", replica_status);
    if (ingested !== undefined) queryParams.append("ingested", ingested);
    if (sort_by) queryParams.append("sort_by", sort_by);
    if (sort_order) queryParams.append("sort_order", sort_order);

    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs?${queryParams.toString()}`;
    return await authFetch(url);
  },

  /**
   * Get MCAP log file by ID
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} logId - Log file ID
   * @returns {Promise} API response with detailed log file data
   */
  async getById(authFetch, logId) {
    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs/${logId}`;
    return await authFetch(url);
  },

  /**
   * Delete MCAP log file
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} logId - Log file ID
   * @returns {Promise} API response
   */
  async delete(authFetch, logId) {
    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs/${logId}`;
    return await authFetch(url, {
      method: "DELETE",
    });
  },

  /**
   * Get a presigned download URL for an MCAP log file (MinIO-backed files only)
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} logId - Log file ID
   * @returns {Promise<string>} Presigned URL
   */
  async getPresignedDownloadUrl(authFetch, logId, storageId) {
    const suffix = storageId
      ? `?storage_id=${encodeURIComponent(storageId)}`
      : "";
    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs/${logId}/download/presigned-url${suffix}`;
    const response = await authFetch(url);
    return response.data;
  },

  /**
   * Download MCAP log file (fallback for non-MinIO storage)
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} logId - Log file ID
   * @returns {Promise} API response with file download
   */
  async download(authFetch, logId) {
    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs/${logId}/download`;
    return await authFetch(url, { responseType: "blob" });
  },

  /**
   * Update MCAP log file metadata
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} logId - Log file ID
   * @param {Object} updateData - Data to update
   * @returns {Promise} API response
   */
  async update(authFetch, logId, updateData) {
    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs/${logId}`;
    return await authFetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });
  },

  /**
   * Get aggregate statistics across all MCAP log files
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response with MCAPStatsResponse data
   */
  async getStats(authFetch, { org_id, ...timeWindow } = {}) {
    const queryParams = new URLSearchParams();
    if (org_id) queryParams.append("org_id", org_id);
    appendTimeWindow(queryParams, timeWindow);
    const qs = queryParams.toString();
    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs/stats${qs ? `?${qs}` : ""}`;
    return await authFetch(url);
  },

  /**
   * Per-UTC-day upload counts and bytes. `days` is the trailing window used
   * when no `uploaded_from` bound is given.
   */
  async getTimeseries(authFetch, { org_id, days = 30, ...timeWindow } = {}) {
    const queryParams = new URLSearchParams();
    if (org_id) queryParams.append("org_id", org_id);
    queryParams.append("days", days);
    appendTimeWindow(queryParams, timeWindow);
    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs/stats/daily-timeseries?${queryParams.toString()}`;
    return await authFetch(url);
  },

  /**
   * Upload MCAP log file
   * @param {function} authFetch - Authenticated fetch function
   * @param {FormData} formData - FormData containing file and metadata
   * @param {function} onProgress - Progress callback function
   * @returns {Promise} API response
   */
  async upload(authFetch, formData, onProgress = null) {
    const url = `${RAAS_DATA_SERVICE_URL}/mcap-logs/upload`;
    
    // Create custom fetch with progress support if onProgress is provided
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Extract token from localStorage (authFetch stores it as "accessToken")
        const token = localStorage.getItem("accessToken") || "";
        
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            onProgress(e);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (e) {
              resolve(xhr.responseText);
            }
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(error);
            } catch (e) {
              reject(new Error(xhr.statusText));
            }
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Network error"));
        });

        xhr.open("POST", url);
        if (token) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }
        xhr.send(formData);
      });
    }

    // Standard upload without progress
    return await authFetch(url, {
      method: "POST",
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary
    });
  },
};
